import { useEffect, useState } from 'react';

import { avvisabili as apiAvvisabili } from '../api/scadenziario.ts';
import type { Avvisabile } from '../api/tipi-dati.ts';

/**
 * Chi va avvisato: le caselle con i nomi dei colleghi.
 *
 * Usato in due posti che sembrano uguali e non lo sono: nel form del **tipo**
 * indica i destinatari *predefiniti*, nel form della **scadenza** quelli veri.
 * La differenza la spiega il testo che gli sta accanto, non il componente.
 *
 * ⚠️ **Chi ha gli avvisi spenti resta selezionabile, ma lo si dice.** Toglierlo
 * dall'elenco sarebbe peggio: la sua assenza non si spiegherebbe, e il giorno
 * che riaccende le notifiche vorrebbe ricevere le scadenze su cui qualcuno
 * l'aveva messo. Il posto dove si riaccendono è il menu del proprio bollino,
 * e nessun altro può farlo per lui.
 */
export function SelettoreDestinatari({
  selezionati,
  onCambia,
  descrizione,
}: {
  selezionati: number[];
  onCambia: (ids: number[]) => void;
  /** La frase sotto al titolo: cambia fra tipo e scadenza. */
  descrizione: string;
}) {
  const [colleghi, setColleghi] = useState<Avvisabile[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;

    apiAvvisabili
      .elenca()
      .then((r) => { if (!annullato) setColleghi(r.elementi); })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      });

    return () => { annullato = true; };
  }, []);

  function commuta(id: number) {
    onCambia(
      selezionati.includes(id) ? selezionati.filter((x) => x !== id) : [...selezionati, id],
    );
  }

  // Quanti fra i selezionati riceveranno davvero qualcosa. È il numero che
  // conta, e non coincide con `selezionati.length` appena qualcuno ha spento
  // gli avvisi.
  const effettivi =
    colleghi === null
      ? selezionati.length
      : selezionati.filter((id) => colleghi.find((c) => c.id === id)?.riceveAvvisi === true).length;

  return (
    /*
     * ⚠️ **`gruppo-spunte` e non `campo`.**
     *
     * `.campo label` impone alle etichette il maiuscoletto grigio da 10 px dei
     * nomi di campo: su un elenco di nomi di persone è illeggibile. Batterlo
     * per specificità funziona, ma è una gara da rigiocare ogni volta che
     * qualcuno aggiunge una regola a `.campo` — e chi la aggiunge non ha modo
     * di sapere che qui dentro c'è qualcosa che non deve riceverla.
     *
     * Uscendo da `.campo` la gara non si gioca proprio.
     */
    <div className="gruppo-spunte">
      <span className="etichetta-gruppo">Chi va avvisato</span>
      <p className="aiuto">{descrizione}</p>

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {colleghi === null ? (
        <p className="aiuto">Caricamento dei colleghi…</p>
      ) : (
        <div className="spunte-destinatari">
          {colleghi.map((c) => (
            <label key={c.id} className="spunta">
              <input
                type="checkbox"
                checked={selezionati.includes(c.id)}
                onChange={() => commuta(c.id)}
              />
              {/*
                ⚠️ Il nome sta in uno `<span>` e non nudo dentro la `<label>`.
                Un testo nudo dentro un contenitore flex diventa un *elemento
                anonimo*, e su quello `text-overflow` non si applica: l'unico
                effetto dell'`overflow: hidden` che lo accompagnava era
                ritagliarlo via del tutto. Sparivano tutti i nomi, con le
                caselle che restavano al loro posto.

                Poi non basta il colore o l'assenza di qualcosa: «avvisi
                spenti» c'è scritto a parole, perché è un'informazione che
                cambia la decisione di chi sta compilando.
              */}
              <span className="spunta-nome">
                {c.nome}
                {!c.riceveAvvisi && (
                  <span className="tenue avviso-spento"> · avvisi spenti</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}

      {/*
        Il riquadro che dice cosa succederà davvero. Sta qui e non fra i
        messaggi d'errore perché non è un errore: una scadenza senza
        destinatari è legittima — a volte è giusta — ma non deve poter passare
        inosservata.
      */}
      {selezionati.length === 0 ? (
        <div className="avviso-attenzione" role="note">
          <strong>Nessuno verrà avvisato di questa scadenza.</strong>
          <p className="aiuto">
            Si può salvare lo stesso, e resterà segnalata nell'elenco e contata nel cruscotto,
            così non ci si dimentica che è rimasta senza.
          </p>
        </div>
      ) : effettivi === 0 ? (
        <div className="avviso-attenzione" role="note">
          <strong>
            {selezionati.length === 1
              ? 'La persona indicata ha gli avvisi spenti.'
              : 'Nessuna delle persone indicate riceve gli avvisi al momento.'}
          </strong>
          <p className="aiuto">
            I nomi restano scritti e torneranno utili appena riaccenderanno le notifiche dal
            proprio profilo, ma <strong>oggi questa scadenza non fa squillare niente</strong> —
            e nell'elenco risulta senza destinatari, esattamente come se fosse vuota.
          </p>
        </div>
      ) : (
        <p className="aiuto">
          {effettivi === 1 ? 'Riceverà l’avviso 1 persona' : `Riceveranno l’avviso ${effettivi} persone`}
          {effettivi !== selezionati.length &&
            ` · ${selezionati.length - effettivi} con gli avvisi spenti`}
          .
        </p>
      )}
    </div>
  );
}
